<style lang="scss">
@import '../../assets/scss/_variables';

.alternative-transport {
    text-align: left;

    .head {
        color: $color-text-2;

        svg {
            font-size: 72px;
        }

        .subtitle {
            margin-top: 5px;

            svg.has-tooltip {
                font-size: 18px;
            }
        }
    }

    .content {
        padding-bottom: 29px;
        font-family: $font-family-2;
        font-weight: bold;
        color: $color-white;

        .amount {
            display: inline-block;
            margin: 17px 0 26px 0;
            position: relative;

            font-size: 72px;
            line-height: 0.9;
        }

            .has-tooltip {
                margin-top: 0.3em;
                position: absolute;
                    top: 50%;
                    right: -1.5em;

                transform: translateY(-52%);
            }

        .unit {
            margin-bottom: 0;
            font-size: 22px;

            i {
                font-weight: $font-weight-2-regular;
                font-style: normal;
            }

            span {
                font-weight: $font-weight-2-regular;
                color: $color-text-2;
            }
        }
    } // .content

    .description {
        border-top: 2px solid $color-border-accent-1-darker;

        font-size: 16px;
        line-height: 1.44;

        p {
            margin-top: 17px;
        }
    }

    @include media-smaller('lg') {
        .head {
            svg {
                font-size: 48px;
            }
        }

        .content {
            .amount {
                font-size: 48px;
            }
        }
    }

    @include media-larger('sm') {
        display: flex;
        flex-flow: column nowrap;
        justify-content: space-between;

        .description {
            height: $grid-size * 2;
        }
    }
}
</style>

<template>
    <div class="alternative-transport">
        <div>
            <div class="head">
                <fa v-if="preset.icon" :icon="preset.icon"></fa>
                <h3 class="subtitle">
                    {{ preset.title }}

                    <template v-if="titleClarification">
                        <template v-if="titleClarification.text">
                            (<span v-html="titleClarification.text" v-tooltip="titleClarification.title"></span>)
                        </template>
                        <fa v-else-if="titleClarification.tooltip" :icon="['fas', 'info-circle']" v-tooltip="titleClarification.tooltip"></fa>
                    </template>
                </h3>
            </div>

            <div class="content">
                <p class="amount">
                    {{ formatUnit(_convert(data.emissions.total, 'g')) }}

                    <fa
                        :icon="['fas', 'info-circle']"
                        v-tooltip="
                            formatUnit(_convert(data.comparedEmissions.total, 'g')) + ' ' +
                            ((isLessEmissions) ? 'less' : 'more') + ' ' +
                            'CO<sub>2</sub> emissions'
                        "
                    ></fa>
                </p>

                <p class="unit">
                    {{ formatNumber(_convert(data.comparedEmissions.kilometer, ['g', 'km']).value) + _convert().unit[0].abbr }}
                    CO<sub>2</sub><span class="separator">/</span><i>{{ _convert().unit[1].abbr }}
                    <span>{{ isMoreEconomic ? 'less' : 'more' }}</span></i>
                </p>

                <p v-if="data.comparedDuration !== 0" class="unit">
                    {{ formatTime(Math.abs(data.comparedDuration * -1000)) }}
                    <span>{{ (data.comparedDuration > 0) ? 'slower' : 'faster' }}</span>
                </p>

                <p v-if="data.comparedDistance !== 0" class="unit">
                    {{ formatUnit(_convert(data.comparedDistance, 'm')) }}
                    <span>{{ (data.comparedDistance > 0) ? 'longer' : 'shorter' }}</span>
                </p>
            </div>
        </div>

        <div v-if="description" class="description">
            <p v-html="description"></p>
        </div>
    </div>
</template>

<script>
import extend from 'extend';
import formatter from '../../mixins/formatter';

export default {
    mixins: [
        formatter
    ],
    props: {
        data: {
            type: Object
        }
    },
    computed: {
        mainTransport () {
            var transportIds = Object.keys(this.data.transports),
                firstId = transportIds[0];

            if (transportIds.length === 1) {
                return extend({}, this.data.transports[firstId], {
                    id: firstId
                });
            }

            return null;
        },
        preset () {
            var data = null,
                presets = this.presets('alternatives');

            if (this.mainTransport && presets[this.mainTransport.id]) {
                data = presets[this.mainTransport.id];
            } else {
                data = presets[this.data.id.toUpperCase()];
            }

            return data || {};
        },
        description () {
            var value = this.preset.description;

            if (!value && this.mainTransport && this.mainTransport.id === 'PLANE') {
                var converted = this._convert(this.data.emissions.kilometer, ['g', 'km']);

                value =
                    'On average, an airplane transports 88 people and a single passenger is responsible for ' +
                    this.formatNumber(converted.value) +
                    ' ' +
                    converted.unit[0].plural.toLowerCase() +
                    ' per ' +
                    converted.unit[1].singular.toLowerCase() +
                    ' of its emissions.';
            }

            return value;
        },
        airports () {
            var values = [];

            if (this.mainTransport && this.mainTransport.id === 'PLANE') {
                if (this.mainTransport.departure_airport) {
                    values.push(this.mainTransport.departure_airport);
                }

                if (this.mainTransport.arrival_airport) {
                    values.push(this.mainTransport.arrival_airport);
                }
            }

            return values;
        },
        titleClarification () {
            if (this.airports.length) {
                var codes = [],
                    names = [];

                this.airports.forEach(function (airport) {
                    var currentCodes = airport.codes;

                    if (currentCodes) {
                        codes.push(currentCodes.iata || currentCodes.icao);
                    }

                    if (airport.name) {
                        names.push(airport.name);
                    }
                });

                return {
                    text: codes.join(' &rarr; '),
                    title: names.join(' to ')
                };
            }

            if (!this.mainTransport) {
                var transports = [];

                for (var k in this.data.transports) {
                    transports.push(k.toLowerCase().replace(/[^a-z]/g, ' '));
                }

                return {
                    tooltip: 'Transports: ' + transports.join(', ')
                };
            }

            return null;
        },
        isLessEmissions () {
            return this.data.comparedEmissions.total < 0;
        },
        isMoreEconomic () {
            return this.data.comparedEmissions.kilometer < 0;
        }
    }
};
</script>
